<template>
  <div class="mask" @mousedown.self="onClose">
    <div class="delete-dialog">
      <!-- ヘッダー -->
      <div class="delete-header">
        <span class="delete-header-title">{{ $t("homePage.novelList.grouping.deleteConfirmTitle") }}</span>
        <button class="delete-close-button" @click="onClose">
          <IconClose class="delete-close-icon" />
        </button>
      </div>

      <div class="delete-body">
        <p class="delete-group-name">{{ groupName }}</p>
        <p class="delete-message">{{ $t("homePage.novelList.grouping.deleteConfirmMessage1") }}</p>
        <p class="delete-message">{{ $t("homePage.novelList.grouping.deleteConfirmMessage2") }}</p>
        <p class="delete-message">{{ $t("homePage.novelList.grouping.deleteConfirmMessage3") }}</p>
      </div>

      <div class="delete-actions">
        <button class="delete-submit-button" @click="onDelete">
          {{ $t("homePage.novelList.grouping.delete") }}
        </button>
        <button class="delete-cancel-button" @click="onClose">
          {{ $t("common.close") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Dialog } from "@/lib/utils";
import SimpleDialog, { SimpleDialogProps } from "@/components/ui/dialogs/SimpleDialog.vue";
import IconClose from "~icons/figma/Close";

export interface NovelGroupDeleteConfirmDialogProps {
  groupId: string;
  groupName: string;
}

export default Vue.extend<Data, Methods, {}, Props>({
  components: { IconClose },
  props: {
    groupId: {
      type: String,
      required: true,
    },
    groupName: {
      type: String,
      required: true,
    },
  },

  methods: {
    async onDelete() {
      try {
        await this.$store.dispatch("novelGroupModule/deleteGroup", {
          groupId: this.groupId,
        });
        this.$close(true);
      } catch (e) {
        const errorDialog = new Dialog(SimpleDialog);
        const errorData: SimpleDialogProps = {
          title: this.$t("homePage.novelList.grouping.errorTitle") as string,
          content: this.$t("homePage.novelList.grouping.errorMessage") as string,
          isError: true,
          width: 350,
        };
        errorDialog.show(errorData);
      }
    },

    onClose() {
      this.$close(null);
    },
  },
});

interface Props {
  groupId: string;
  groupName: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface Data {}

interface Methods {
  onDelete(): Promise<void>;
  onClose(): void;
}
</script>

<style lang="scss" scoped>
.mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  z-index: 8002;
  align-items: center;
  justify-content: center;
  background-color: var(--color-overlay);
}

.delete-dialog {
  background-color: var(--color-background-normal);
  border-radius: var(--radius-md);
  width: 340px;
  max-width: 90%;
  display: flex;
  flex-direction: column;
}

.delete-header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 20px 24px 12px;
}

.delete-header-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.delete-close-button {
  position: absolute;
  right: 16px;
  top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.delete-close-icon {
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
}

.delete-body {
  padding: 8px 24px 0;
}

.delete-group-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  margin: 0 0 12px;
  text-align: center;
}

.delete-message {
  font-size: var(--font-size-xs);
  color: var(--color-text-normal);
  margin: 0 0 4px;
  line-height: var(--line-height-default);
}

.delete-actions {
  padding: 12px 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.delete-submit-button {
  width: 100%;
  padding: 10px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-on-fixed-bg);
  background-color: var(--color-warning);
  border: none;
  border-radius: var(--radius-rounded);
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
}

.delete-cancel-button {
  width: 100%;
  padding: 10px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  background-color: var(--color-surface-muted);
  border: none;
  border-radius: var(--radius-rounded);
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
}
</style>
